import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import LoadingIcon from './LoadingIcon'
import UpdatePolicyExternalLinkModal from './UpdatePolicyExternalLinkModal'
import DutyWhenUpdatingPolicyModal from './DutyWhenUpdatingPolicyModal'
import PremiumWarningModal from './PremiumWarningModal'
import PolicyTabNav from './PolicyTabNav'
import { useApi } from '../services/api'
import type { PolicyLink } from '../services/types'
import {
  getHeaderVehicleLine,
  getPolicyVehicleValueRowParts,
  instalmentRowFromPolicy,
  policyTabTermPremiumLabel,
} from '../utils/policyDisplay'
import PolicyReferralBlocked from './PolicyReferralBlocked'
import {
  getPolicyReferralMessageFromApiError,
  isPolicyPortalBlockedApiError,
  isReferralBlockedPolicySummary,
  isPolicyUpdatesBlockedSummary,
  splitPolicyUpdatesBlockedAlertCopy,
} from '../utils/policyReferralState'
import {
  getInsuredAssetClass,
  policyIsNonPoweredVehicle,
  policySupportsOnlineDetailUpdate,
  policyIsUnsupportedVehicleType,
} from '../utils/policyType'
import { useScrollToTopOnErrorSignal } from '../hooks/useScrollToTopOnErrorSignal'
import { InfoAlert, PhoneIcon } from './ui'

function ChevronLeftIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
    </svg>
  )
}

function ChevronDownIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function EditIcon() {
  return (
    <img 
      src="/pencil_icon.svg" 
      alt="Edit" 
      className="w-5 h-5"
    />
  )
}

/** Display state for policy view; all display fields are strings, has4X4Vehicle is boolean */
interface PolicyDisplayData {
  regoNumber: string
  vehicleName: string
  /** Short year/make/model for header only (e.g. "2024 Toyota Landcruiser") */
  headerVehicleLine: string
  renewalDate: string
  policyNumber: string
  makeAndModel: string
  coverDates: string
  premium: string
  instalmentRow: { name: string; value: string } | null
  paymentFrequency: string
  autoRenewal: string
  basicExcess: string
  /** Row title: "Agreed value" or "Market value" (replaces generic vehicle value). */
  vehicleValueLabel: string
  vehicleValue: string
  modificationsValue: string
  registrationNumber: string
  parkingAddress: string
  overnightParking: string
  vehicleUse: string
  vehicleFinance: string
  mainDriver: string
  listedDrivers: string
  policyContact: string
  email: string
  mobileNumber: string
  postalAddress: string
  authorisedPeople: string
  offRoadRecovery: string
  carHire: string
  excessFreeGlass: string
  roadsideAssistance: string
  personalEffects: string
  annexCover: string
  annexCoverExcess: string
  remainingInstalments: string
  has4X4Vehicle?: boolean
  /** Insured asset label when not a 4x4 (e.g. "Caravan"); only used for header badge */
  non4x4InsuredAssetLabel?: string
  /** Caravan / camper / fifth wheel / slide-on (not 4x4 motor or daily drive). */
  isNonPoweredVehicle?: boolean
}

function formatRegistrationWithState(
  registrationNumber: string | undefined,
  registrationState: string | undefined
): string {
  const rego = String(registrationNumber ?? '').trim()
  if (!rego) return 'N/A'
  const state = String(registrationState ?? '').trim().toUpperCase()
  if (/^(NSW|VIC|QLD|WA|SA|TAS|ACT|NT)$/.test(state)) {
    return `${rego} (${state})`
  }
  return rego
}

interface DataItemProps {
  name: string
  value: string
  editType?: 'external' | 'pencil' | null
  onEdit?: () => void
}

function DataItem({ name, value, editType = null, onEdit }: DataItemProps) {
  const getIcon = () => {
    if (editType === 'external') return <PhoneIcon className="w-5 h-5" />
    return <EditIcon />
  }

  const getLabel = () => (editType === 'external' ? 'Update' : 'Edit')

  return (
    <div className="border-t border-border-primary flex gap-[12px] md:gap-[12px] items-center px-[6px] py-[16px] relative w-full">
      <div className="flex flex-col gap-[6px] items-start flex-1">
        <p className="font-body font-regular text-md md:text-lg leading-md md:leading-lg text-text-tertiary-600 w-full">
          {name}
        </p>
        <p className="font-body font-semibold text-md md:text-lg leading-md md:leading-lg text-text-primary-900 w-full">
          {value}
        </p>
      </div>
      {editType && (
        <button
          type="button"
          onClick={onEdit}
          aria-label={editType === 'external' ? `Call us to update ${name}` : getLabel()}
          className="flex gap-[6px] items-center justify-center overflow-clip relative shrink-0"
        >
          <div
            className={`overflow-clip relative shrink-0 size-5 ${
              editType === 'pencil' ? '' : 'text-foreground-brand-secondary-alt'
            }`}
          >
            {getIcon()}
          </div>
          <p className="font-body font-semibold text-md leading-md text-text-brand-secondary-700">
            {getLabel()}
          </p>
        </button>
      )}
    </div>
  )
}

interface CollapsibleSectionProps {
  icon: React.ReactNode
  title: string
  children: React.ReactNode
  isOpen?: boolean
  onToggle?: () => void
}

function CollapsibleSection({ icon, title, children, isOpen = false, onToggle }: CollapsibleSectionProps) {
  return (
    <div className="bg-background-primary border border-border-secondary border-solid flex flex-col gap-[12px] items-start px-[16px] py-[12px] relative rounded-lg shadow-xs w-full">
      <button
        onClick={onToggle}
        className="flex gap-[12px] items-center relative shrink-0 w-full text-left"
      >
        <div className="bg-background-tertiary overflow-clip relative rounded-full shrink-0 size-12">
          <div className="absolute left-3 top-3 overflow-clip size-6 text-text-tertiary-600">
            {icon}
          </div>
        </div>
        <p className="flex-1 font-display font-medium text-display-xs leading-[32px] text-text-primary-900 min-w-0 overflow-ellipsis overflow-hidden whitespace-nowrap">
          {title}
        </p>
        <div className={`overflow-clip relative shrink-0 size-4 text-text-tertiary-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}>
          <ChevronDownIcon />
        </div>
      </button>
      {isOpen && (
        <div className="flex flex-col items-start relative shrink-0 w-full">
          {children}
        </div>
      )}
    </div>
  )
}

/** Build display policyData from API/PolicyLink shape (shared for state.policy and getPolicy response) */
function policyToDisplayData(policy: PolicyLink): PolicyDisplayData {
  const policyNumber = policy.policyNumber || policy.policyRef
  const regoOnly = policy.registrationNumber || policyNumber
  const regoStateForDisplay = policyIsNonPoweredVehicle(policy) ? undefined : policy.registrationState
  const regoNumber = formatRegistrationWithState(regoOnly, regoStateForDisplay)
  const vehicleName = policy.vehicleName || `Policy ${policy.policyRef}`
  const headerVehicleLine = getHeaderVehicleLine(policy)
  let renewalDate: string
  if (policy.renewalDateFormatted) {
    renewalDate = policy.renewalDateFormatted
  } else if (policy.renewalDate) {
    renewalDate = new Date(policy.renewalDate).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric'
    })
  } else {
    const linkedDate = new Date(policy.linkedAt)
    linkedDate.setFullYear(linkedDate.getFullYear() + 1)
    renewalDate = linkedDate.toLocaleDateString('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric'
    })
  }
  let coverDates: string
  if (policy.coverStartDate && policy.coverEndDate) {
    coverDates = `${new Date(policy.coverStartDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })} – ${new Date(policy.coverEndDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`
  } else {
    coverDates = 'N/A'
  }
  let premium: string
  if (policy.premium !== undefined && policy.premium !== null) {
    // Term total from POLSUM (not per-instalment). Payment cadence is policyData.paymentFrequency.
    premium = `$${policy.premium.toFixed(2)}`
  } else {
    premium = 'N/A'
  }
  const paymentFrequency = policy.paymentFrequency
    ? policy.paymentFrequency.charAt(0) + policy.paymentFrequency.slice(1).toLowerCase()
    : 'N/A'
  const autoRenewal = policy.autoRenewal || 'N/A'
  let remainingInstalments = ''
  if (policy.remainingInstalments !== null && policy.remainingInstalments !== undefined) {
    const count = policy.remainingInstalments
    const freqLabel = policy.paymentFrequency === 'MONTHLY' ? 'monthly' : policy.paymentFrequency === 'QUARTERLY' ? 'quarterly' : 'annual'
    remainingInstalments = `${count} ${freqLabel} payment${count !== 1 ? 's' : ''}`
  }
  const basicExcess = policy.basicExcess !== undefined && policy.basicExcess !== null ? `$${policy.basicExcess}` : 'N/A'
  const { rowLabel: vehicleValueLabel, rowValue: vehicleValue } = getPolicyVehicleValueRowParts(policy)
  const modificationsValue = policy.modificationsValue !== undefined && policy.modificationsValue !== null
    ? (policy.modificationsValue === 0
        ? 'No modifications'
        : `$${policy.modificationsValue.toLocaleString('en-AU', { maximumFractionDigits: 0 })}`)
    : 'N/A'
  return {
    regoNumber,
    vehicleName,
    headerVehicleLine,
    renewalDate,
    policyNumber,
    makeAndModel: policy.makeAndModel || vehicleName || 'N/A',
    coverDates,
    premium,
    instalmentRow: instalmentRowFromPolicy(policy),
    paymentFrequency,
    autoRenewal,
    basicExcess,
    vehicleValueLabel,
    vehicleValue,
    modificationsValue,
    registrationNumber: formatRegistrationWithState(
      policy.registrationNumber || regoOnly,
      regoStateForDisplay
    ),
    parkingAddress: policy.parkingAddress || 'N/A',
    overnightParking: policy.overnightParking || 'N/A',
    vehicleUse: policy.vehicleUse || 'N/A',
    vehicleFinance: policy.vehicleFinance || 'N/A',
    mainDriver: policy.mainDriver || 'N/A',
    listedDrivers: policy.listedDrivers && policy.listedDrivers.length > 0 ? policy.listedDrivers.join(', ') : 'N/A',
    policyContact: policy.policyContact || 'N/A',
    email: policy.email || 'N/A',
    mobileNumber: policy.mobileNumber || 'N/A',
    postalAddress: policy.postalAddress || 'N/A',
    authorisedPeople: policy.authorisedPeople || 'N/A',
    offRoadRecovery: policy.offRoadRecovery || 'N/A',
    carHire: policy.carHire || 'N/A',
    excessFreeGlass: (() => {
      const raw = policy.excessFreeGlass
      if (policyIsNonPoweredVehicle(policy)) {
        if (raw === 'Yes') return 'Add cover'
        if (raw === 'No') return 'Not included'
      }
      return raw || 'N/A'
    })(),
    roadsideAssistance: policy.roadsideAssistance || 'N/A',
    personalEffects: (() => {
      const raw = policy.personalEffects
      if (policyIsNonPoweredVehicle(policy) && raw && raw !== 'No') {
        if (raw === '$2,000') return '$2,000 included'
        if (raw === '$5,000') return 'Up to $5,000'
        if (raw === '$10,000') return 'Up to $10,000'
      }
      return raw || 'N/A'
    })(),
    annexCover: (() => {
      const raw = policy.annexCover
      if (raw && raw !== 'Not covered') {
        if (raw === '$2,000') return '$2,000 included'
        if (raw === '$5,000') return 'Up to $5,000'
        if (raw === '$7,500') return 'Up to $7,500'
      }
      return raw || 'Not covered'
    })(),
    annexCoverExcess: policy.annexCoverExcess || 'None',
    remainingInstalments,
    has4X4Vehicle: getInsuredAssetClass(policy) === '4x4',
    non4x4InsuredAssetLabel: policy.non4x4InsuredAssetLabel,
    isNonPoweredVehicle: policyIsNonPoweredVehicle(policy),
  }
}

export default function PolicyView() {
  const navigate = useNavigate()
  const location = useLocation()
  const { policyId } = useParams()
  const { getPolicy } = useApi()
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ policy: true })
  const [showExternalLinkModal, setShowExternalLinkModal] = useState(false)
  const [showDutyModal, setShowDutyModal] = useState(false)
  const [showPremiumWarningModal, setShowPremiumWarningModal] = useState(false)
  const [editingField, setEditingField] = useState<string>('')
  const [policyData, setPolicyData] = useState<PolicyDisplayData | null>(null)
  /** Latest getPolicy result for passing into the claims flow without a duplicate list fetch */
  const [policyForClaimsNav, setPolicyForClaimsNav] = useState<PolicyLink | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [referralMessage, setReferralMessage] = useState<string | null>(null)
  useScrollToTopOnErrorSignal(error)

  // Scroll to top when component mounts, unless navigating from sidebar tabs
  useEffect(() => {
    if (!location.state?.preserveScroll) {
      window.scrollTo(0, 0)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- only check on mount
  }, [])

  // Fetch policy data from API (or use passed state.policy from Dashboard for instant display)
  useEffect(() => {
    let cancelled = false

    const fetchPolicyData = async () => {
      if (!policyId) {
        setLoading(false)
        return
      }

      const decodedPolicyRef = decodeURIComponent(policyId)

      // Always fetch from API for latest policy data. State from Dashboard may be stale.
      try {
        setLoading(true)
        setError(null)
        setReferralMessage(null)

        const fromNav = location.state && typeof location.state === 'object' && 'policy' in location.state
          ? (location.state as { policy?: PolicyLink }).policy
          : undefined
        if (isReferralBlockedPolicySummary(fromNav)) {
          if (!cancelled) {
            setReferralMessage(fromNav?.referralMessage ?? null)
            setPolicyData(null)
            setPolicyForClaimsNav(null)
            setLoading(false)
          }
          return
        }

        const policy = await getPolicy(decodedPolicyRef)

        if (cancelled) return

        if (isReferralBlockedPolicySummary(policy)) {
          setReferralMessage(policy.referralMessage ?? null)
          setPolicyData(null)
          setPolicyForClaimsNav(null)
          setLoading(false)
          return
        }

        setPolicyData(policyToDisplayData(policy))
        setPolicyForClaimsNav(policy)
        setLoading(false)
      } catch (error) {
        if (isPolicyPortalBlockedApiError(error)) {
          if (!cancelled) {
            setReferralMessage(getPolicyReferralMessageFromApiError(error))
            setError(null)
            setPolicyData(null)
            setPolicyForClaimsNav(null)
          }
        } else {
          const message = error instanceof Error ? error.message : 'Failed to load policy'
          console.error(
            `Policy fetch failed for policy ${policyId ?? 'unknown'} (policy is linked to your account but the API returned an error):`,
            error
          )
          if (!cancelled) {
            setError(
              `This policy is linked to your account but we couldn't load its details. ${message} Please try again or contact support.`
            )
            setPolicyData(null)
            setPolicyForClaimsNav(null)
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    fetchPolicyData()
    
    return () => {
      cancelled = true
    }
  }, [policyId, location.state]) // policyId + dashboard may pass referral summary in state

  // Show loading state while fetching policy data.
  // Match dashboard layout: same header + main content area so "white space" size is preserved.
  if (referralMessage != null) {
    return (
      <PolicyReferralBlocked
        message={referralMessage}
        onBackToDashboard={() => {
          sessionStorage.removeItem('club4x4_policies_cache')
          navigate('/dashboard')
        }}
      />
    )
  }

  if (loading) {
    return (
      <div className="bg-background-tertiary flex flex-col min-h-screen w-full">
        <DashboardNavigation />

        {/* Header - same height as dashboard so layout matches */}
        <div className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[32px] md:py-[48px] relative shrink-0 w-full">
          <div className="flex flex-col gap-0 items-center md:items-start max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
            <button
              onClick={() => {
                sessionStorage.removeItem('club4x4_policies_cache')
                navigate('/dashboard')
              }}
              className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0"
            >
              <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
                <ChevronLeftIcon />
              </div>
              <p className="font-body font-semibold text-sm leading-sm text-text-primary-on-brand">
                Back to dashboard
              </p>
            </button>
          </div>
        </div>

        <main id="main" className="flex flex-col flex-1 min-h-0 w-full px-[16px] md:px-[32px] py-[40px] md:py-[96px] max-w-container-desktop mx-auto">
          <h1 className="sr-only">Policy</h1>
          <div className="flex flex-1 min-h-0 w-full items-center justify-center">
            <LoadingIcon size="medium" message="Loading your policy..." />
          </div>
        </main>

        <Footer />
      </div>
    )
  }

  if (error || !policyData) {
    return (
      <div className="bg-background-tertiary flex flex-col min-h-screen w-full">
        <DashboardNavigation />

        {/* Header - same height as dashboard so layout matches */}
        <div className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[32px] md:py-[48px] relative shrink-0 w-full">
          <div className="flex flex-col gap-0 items-center md:items-start max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
            <button
              onClick={() => {
                sessionStorage.removeItem('club4x4_policies_cache')
                navigate('/dashboard')
              }}
              className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0"
            >
              <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
                <ChevronLeftIcon />
              </div>
              <p className="font-body font-semibold text-sm leading-sm text-text-primary-on-brand">
                Back to dashboard
              </p>
            </button>
          </div>
        </div>

        <main id="main" className="flex flex-col flex-1 min-h-0 w-full px-[16px] md:px-[32px] py-[40px] md:py-[96px] max-w-container-desktop mx-auto">
          <h1 className="sr-only">Policy</h1>
          <div className="bg-background-primary border border-border-primary border-solid flex flex-col items-center justify-center px-[24px] py-[40px] relative rounded-2xl shadow-xs w-full">
            <p className="font-body font-semibold text-md leading-md text-text-primary-900 mb-2">
              Error loading policy
            </p>
            <p className="font-body font-regular text-sm leading-sm text-text-tertiary-600">
              {error || 'Policy data is unavailable.'}
            </p>
          </div>
        </main>

        <Footer />
      </div>
    )
  }

  const handleUpdatePolicy = () => {
    if (!policyId) return
    setEditingField('')
    setShowPremiumWarningModal(true)
  }

  const handleEdit = (field: string, editType: 'external' | 'pencil') => {
    setEditingField(field)
    if (editType === 'external') {
      setShowExternalLinkModal(true)
    } else if (
      policyForClaimsNav &&
      !policySupportsOnlineDetailUpdate(policyForClaimsNav)
    ) {
      // Non-4x4 asset types we do not self-serve online (e.g. fifth wheeler)
      setShowExternalLinkModal(true)
    } else {
      if (policyId) {
        setShowPremiumWarningModal(true)
      }
    }
  }

  const toggleSection = (sectionName: string) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }))
  }

  const isUnsupportedType = policyIsUnsupportedVehicleType(policyForClaimsNav)
  const policyUpdatesBlocked = isPolicyUpdatesBlockedSummary(policyForClaimsNav)
  const ed = (t: 'external' | 'pencil'): 'external' | 'pencil' | undefined =>
    isUnsupportedType || policyUpdatesBlocked ? undefined : t
  const updatesBlockedAlert = policyUpdatesBlocked
    ? splitPolicyUpdatesBlockedAlertCopy(policyForClaimsNav?.policyUpdatesBlockedMessage)
    : null

  return (
    <div className="bg-background-brand-primary flex flex-col items-start relative min-h-screen w-full">
      <DashboardNavigation />

      <main id="main" className="flex w-full flex-col items-start">
      {/* Header Section */}
      <div className="bg-background-brand-solid flex flex-col gap-0 items-center overflow-clip px-0 py-[32px] relative shrink-0 w-full">
        <div className="flex flex-col gap-0 items-center md:items-start max-w-container-desktop px-[16px] md:px-[32px] py-0 relative shrink-0 w-full">
          <div className="flex flex-col gap-[24px] items-start max-w-[768px] relative shrink-0 w-full">
            {/* Back button */}
            <button
              onClick={() => {
                sessionStorage.removeItem('club4x4_policies_cache')
                navigate('/dashboard')
              }}
              className="cursor-pointer flex gap-[4px] items-center justify-center overflow-clip p-0 relative shrink-0"
            >
              <div className="overflow-clip relative shrink-0 size-5 text-text-primary-on-brand">
                <ChevronLeftIcon />
              </div>
              <p className="font-body font-semibold text-sm leading-sm text-text-primary-on-brand">
                Back to dashboard
              </p>
            </button>

            {/* Policy Summary Card - Displayed on teal background */}
            <div className="flex flex-col gap-[12px] items-start relative shrink-0 w-full">
              {/* Rego badge + insured asset type (non-4x4 only) */}
              <div className="content-start flex flex-wrap gap-[8px] items-center relative shrink-0 w-full">
                <div className="bg-background-primary border border-text-primary-900 border-solid flex items-center px-[8px] py-[4px] relative rounded-xs shrink-0">
                  <p className="font-body font-medium text-md leading-md text-text-primary-900 text-center">
                    {policyData.regoNumber}
                  </p>
                </div>
                {policyData.has4X4Vehicle === false && (
                  <span className="font-body font-medium text-sm leading-sm text-text-primary-on-brand bg-white/20 px-[8px] py-[4px] rounded-xs">
                    {policyData.non4x4InsuredAssetLabel?.trim() || 'Non-4x4 policy'}
                  </span>
                )}
              </div>

              {/* Vehicle Info */}
              <div className="flex flex-col gap-[4px] items-start relative shrink-0 w-full">
                <div className="flex gap-[8px] items-center relative shrink-0 w-full">
                  <h1 className="m-0 flex-1 min-w-0 relative shrink-0 font-display text-display-sm font-regular leading-[38px] text-text-primary-on-brand">
                    {policyData.headerVehicleLine}
                  </h1>
                </div>
                <div className="content-start flex flex-wrap gap-[6px] items-start leading-sm md:leading-md relative shrink-0 text-text-primary-on-brand text-sm md:text-md w-full">
                  <p className="font-body font-regular relative shrink-0">
                    Renews on{' '}
                  </p>
                  <p className="font-body font-medium relative shrink-0">
                    {policyData.renewalDate}
                  </p>
                </div>
                <div className="content-start flex flex-wrap gap-[6px] items-start leading-sm md:leading-md relative shrink-0 text-text-primary-on-brand text-sm md:text-md w-full">
                  <p className="font-body font-regular relative shrink-0">
                    Policy Number{' '}
                  </p>
                  <p className="font-body font-semibold relative shrink-0">
                    {policyData.policyNumber}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col md:flex-row gap-[32px] items-start max-w-container-desktop px-[16px] md:px-[32px] py-[32px] md:py-[48px] relative shrink-0 w-full mx-auto">
        <PolicyTabNav policyId={policyId} activeTab="policy" policyForClaimsNav={policyForClaimsNav} />

        {/* Content + premium notice — side-by-side only at xl+; md–lg stack so tablet width is not squeezed */}
        <div className="flex flex-col xl:flex-row gap-[20px] items-start flex-1 min-w-0 w-full">
        <div className="flex flex-1 flex-col gap-[32px] items-center max-w-[768px] min-w-0 relative shrink-0 w-full">
            {/* Update Policy Button (4x4, caravan, camper trailer; other non-4x4 types use Call us) */}
            {policyForClaimsNav &&
              !policyUpdatesBlocked &&
              policySupportsOnlineDetailUpdate(policyForClaimsNav) && (
              <button
                onClick={handleUpdatePolicy}
                className="bg-background-brand-solid flex gap-[6px] items-center justify-center overflow-clip px-[18px] py-[12px] relative rounded-md shadow-xs shrink-0 w-full"
              >
                <div className="overflow-clip relative shrink-0 size-5 text-foreground-brand-secondary-alt">
                  <EditIcon />
                </div>
                <span className="font-body font-semibold text-md text-text-white">
                  Update policy
                </span>
              </button>
            )}

            {(updatesBlockedAlert || !isUnsupportedType) && (
              <div className="xl:hidden w-full">
                {updatesBlockedAlert ? (
                  <InfoAlert
                    message={updatesBlockedAlert.message}
                    description={updatesBlockedAlert.description}
                  />
                ) : (
                  <InfoAlert
                    message="Making changes to your policy may affect your premium"
                    description="Some policy changes cannot be made online. To make changes that are not listed here, call us on 1300 296 296."
                  />
                )}
              </div>
            )}

            {/* Policy Section */}
            <CollapsibleSection
              icon={
                <img 
                  src="/Icon-policy.svg" 
                  alt="Policy" 
                  className="w-6 h-6"
                />
              }
              title="Policy"
              isOpen={openSections.policy}
              onToggle={() => toggleSection('policy')}
            >
              <DataItem name="Policy number" value={policyData.policyNumber} />
              <DataItem name="Term Date" value={policyData.coverDates} />
              {policyData.instalmentRow && (
                <DataItem name={policyData.instalmentRow.name} value={policyData.instalmentRow.value} />
              )}
              <DataItem name={policyTabTermPremiumLabel(policyData.paymentFrequency)} value={policyData.premium} />
              <DataItem name="Payment frequency" value={policyData.paymentFrequency} editType={ed('external')} onEdit={() => handleEdit('Payment frequency', 'external')} />
              <DataItem name="Auto renewal" value={policyData.autoRenewal} editType={ed('external')} onEdit={() => handleEdit('Auto renewal', 'external')} />
              <DataItem name="Basic excess" value={policyData.basicExcess} editType={ed('external')} onEdit={() => handleEdit('Basic excess', 'external')} />
              <DataItem name={policyData.vehicleValueLabel} value={policyData.vehicleValue} editType={ed('external')} onEdit={() => handleEdit(policyData.vehicleValueLabel, 'external')} />
              <DataItem name="Modifications value" value={policyData.modificationsValue} editType={ed('pencil')} onEdit={() => handleEdit('Modifications value', 'pencil')} />
            </CollapsibleSection>

            {/* Vehicle Section */}
            <CollapsibleSection
              icon={
                <img 
                  src="/Vehicle-Icon.svg" 
                  alt="Vehicle" 
                  className="w-6 h-6"
                />
              }
              title="Vehicle"
              isOpen={openSections.vehicle}
              onToggle={() => toggleSection('vehicle')}
            >
              <DataItem name="Make and model" value={policyData.makeAndModel} />
              <DataItem name="Registration number" value={policyData.registrationNumber} editType={ed('pencil')} onEdit={() => handleEdit('Registration number', 'pencil')} />
              <DataItem name="Parking address" value={policyData.parkingAddress} editType={ed('pencil')} onEdit={() => handleEdit('Parking address', 'pencil')} />
              <DataItem name="Overnight parking location" value={policyData.overnightParking} editType={ed('pencil')} onEdit={() => handleEdit('Overnight parking location', 'pencil')} />
              {/* Vehicle use is not applicable for NPV (caravan/camper) or unsupported types (fifth wheeler/slide-on) */}
              {!policyData.isNonPoweredVehicle && !isUnsupportedType && (
                <DataItem
                  name="Vehicle use"
                  value={policyData.vehicleUse}
                  editType={ed('external')}
                  onEdit={() => handleEdit('Vehicle use', 'external')}
                />
              )}
              <DataItem name="Vehicle finance" value={policyData.vehicleFinance} editType={ed('external')} onEdit={() => handleEdit('Vehicle finance', 'external')} />
            </CollapsibleSection>

            {/* Drivers Section */}
            <CollapsibleSection
              icon={
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                </svg>
              }
              title="Drivers"
              isOpen={openSections.drivers}
              onToggle={() => toggleSection('drivers')}
            >
              <DataItem name="Main driver" value={policyData.mainDriver} />
              <DataItem
                name="Listed drivers"
                value={policyData.listedDrivers}
                editType={ed('external')}
                onEdit={() => handleEdit('Listed drivers', 'external')}
              />
            </CollapsibleSection>

            {/* Contact Details Section */}
            <CollapsibleSection
              icon={
                <img 
                  src="/Contact-details-Icon.svg" 
                  alt="Contact details" 
                  className="w-6 h-6"
                />
              }
              title="Contact details"
              isOpen={openSections.contact}
              onToggle={() => toggleSection('contact')}
            >
              <DataItem name="Policy contact" value={policyData.policyContact} editType={ed('external')} onEdit={() => handleEdit('Policy contact', 'external')} />
              <DataItem name="Email" value={policyData.email} editType={ed('pencil')} onEdit={() => handleEdit('Email', 'pencil')} />
              <DataItem name="Mobile number" value={policyData.mobileNumber} editType={ed('pencil')} onEdit={() => handleEdit('Mobile number', 'pencil')} />
              <DataItem name="Postal address" value={policyData.postalAddress} editType={ed('pencil')} onEdit={() => handleEdit('Postal address', 'pencil')} />
              <DataItem name="Authorised people" value={policyData.authorisedPeople} editType={ed('external')} onEdit={() => handleEdit('Authorised people', 'external')} />
            </CollapsibleSection>

            {/* Policy Extras Section */}
            <CollapsibleSection
              icon={
                <img 
                  src="/Policy-extras.svg" 
                  alt="Policy extras" 
                  className="w-6 h-6"
                />
              }
              title="Policy extras"
              isOpen={openSections.extras}
              onToggle={() => toggleSection('extras')}
            >
              <DataItem name="Off road recovery cover" value={policyData.offRoadRecovery} editType={ed('pencil')} onEdit={() => handleEdit('Off road recovery cover', 'pencil')} />
              {!policyData.isNonPoweredVehicle && (
                <DataItem name="Car hire" value={policyData.carHire} editType={ed('external')} onEdit={() => handleEdit('Car hire', 'external')} />
              )}
              <DataItem name="Excess-free glass cover" value={policyData.excessFreeGlass} editType={ed('external')} onEdit={() => handleEdit('Excess-free glass cover', 'external')} />
              {!policyData.isNonPoweredVehicle && (
                <DataItem name="Roadside assistance" value={policyData.roadsideAssistance} editType={ed('external')} onEdit={() => handleEdit('Roadside assistance', 'external')} />
              )}
              <DataItem name="Personal effects cover" value={policyData.personalEffects} editType={ed('external')} onEdit={() => handleEdit('Personal effects cover', 'external')} />
              {policyData.isNonPoweredVehicle && (
                <>
                  <DataItem name="Annexe cover" value={policyData.annexCover} editType={ed('external')} onEdit={() => handleEdit('Annexe cover', 'external')} />
                  <DataItem name="Annexe cover excess" value={policyData.annexCoverExcess} editType={ed('external')} onEdit={() => handleEdit('Annexe cover excess', 'external')} />
                </>
              )}
            </CollapsibleSection>
          </div>

          {(updatesBlockedAlert || !isUnsupportedType) && (
            <aside className="hidden xl:block shrink-0 w-full xl:max-w-[280px] xl:sticky xl:top-[32px]">
              {updatesBlockedAlert ? (
                <InfoAlert
                  message={updatesBlockedAlert.message}
                  description={updatesBlockedAlert.description}
                />
              ) : (
                <InfoAlert
                  message="Making changes to your policy may affect your premium"
                  description="Some policy changes cannot be made online. To make changes that are not listed here, call us on 1300 296 296."
                />
              )}
            </aside>
          )}
        </div>

      </div>
      </main>

      <Footer />

      {/* Modals */}
      {showExternalLinkModal && (
        <UpdatePolicyExternalLinkModal
          fieldName={editingField}
          onClose={() => setShowExternalLinkModal(false)}
        />
      )}

      {showDutyModal && (
        <DutyWhenUpdatingPolicyModal
          onClose={() => setShowDutyModal(false)}
          fieldName={editingField}
          policyId={policyId}
        />
      )}

      {showPremiumWarningModal && policyId && (
        <PremiumWarningModal
          onClose={() => setShowPremiumWarningModal(false)}
          onConfirm={() => {
            setShowPremiumWarningModal(false)
            const base = `/policy/${encodeURIComponent(policyId)}`
            setEditingField('')
            navigate(`${base}/update-all`)
          }}
          confirmLabel="Continue"
        />
      )}
    </div>
  )
}

